import { parseISO } from "date-fns";
import { formatDate } from "./formatDate";
import { formatDateTime } from "./formatDateTime";

/**
 * The day an API date-time falls on in the scheme's time zone, wherever the
 * viewer is: the API writes each date-time in that zone
 * ("2026-01-01T10:00:00+10:00"), so the date and offset it is written with are
 * the scheme's. Both are undefined for a string without an offset.
 */
const dayOf = (iso: string) => {
  const [, date, offset] =
    iso.match(/^(\d{4}-\d{2}-\d{2})T.*?(Z|[+-]\d{2}:?\d{2})$/) ?? [];
  return { date, offset };
};

/**
 * An API date-time's day in the scheme's time zone, as dd/MM/yyyy. Any string
 * with an offset counts as the API's, so a Date's toISOString() would give its
 * UTC day: pass the Date itself. A Date, or a string without an offset, gives
 * the viewer's day of `new Date(value)`; "" if that is invalid.
 */
export const formatSchemeDay = (value: string | Date) => {
  const day = typeof value === "string" && dayOf(value).date;
  return formatDate(day ? parseISO(day) : new Date(value));
};

/**
 * An accounting period by the scheme's days, as "dd/MM/yyyy → dd/MM/yyyy",
 * wherever the viewer is; "-" unless both bounds are given.
 */
export const formatSchemePeriod = (period?: {
  periodStart?: string;
  periodEnd?: string;
}) =>
  period?.periodStart && period.periodEnd
    ? `${formatSchemeDay(period.periodStart)} → ${formatSchemeDay(period.periodEnd)}`
    : "-";

/**
 * An API date-time's date and time as written, as dd/MM/yyyy h:mm a: the
 * scheme's, wherever the viewer is. "" if it is invalid.
 */
export const formatSchemeDateTime = (value: string) => {
  const { offset = "" } = dayOf(value);
  // ponytail: parses the written time as the viewer's, so one inside the
  // viewer's spring-forward gap reads an hour late; format the string's
  // fields directly if that matters.
  return formatDateTime(parseISO(value.slice(0, value.length - offset.length)));
};
